import { useState } from 'react'
import { useDashReport } from '../hooks/useDashReport'
import { HorizontalBarChart } from '../components'

interface ItemConPct {
  nombre: string
  total: number
  porcentaje: number
}

interface PaisData {
  total: number
  por_especialidad: ItemConPct[]
  por_provincia: ItemConPct[]
  por_cadena: ItemConPct[]
}

interface GskReport {
  titulo: string
  subtitulo: string
  paises: {
    Ecuador: PaisData
    Peru: PaisData
  }
}

export function GskBd0726Page() {
  const { data, isLoading, error } = useDashReport<GskReport>('gsk/bd-0726')
  const [pais, setPais] = useState<'Ecuador' | 'Peru'>('Ecuador')
  const [cadenasOpen, setCadenasOpen] = useState(false)
  const [provinciasOpen, setProvinciasOpen] = useState(false)
  const [espOpen, setEspOpen] = useState(false)

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
        </div>
      </div>
    )
  }

  const d = data.paises[pais]

  const chartCadenas = d.por_cadena
    .slice(0, 10)
    .map(c => ({ label: c.nombre, value: c.total, color: '#2fbc80' }))

  const chartProvincias = d.por_provincia
    .slice(0, 10)
    .map(p => ({ label: p.nombre, value: p.total, color: '#57a6d8' }))

  const chartEspecialidades = d.por_especialidad
    .map((e, i) => ({ label: e.nombre, value: e.total, color: i === 0 ? '#8d82bc' : '#eb6587' }))

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <a
              href="https://enterprise.tonalstrategies.com/pricing/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              Calculador
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>

        {/* Tabs de país */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b border-gray-200 px-4">
            {(['Ecuador', 'Peru'] as const).map(p => (
              <button
                key={p}
                onClick={() => {
                  setPais(p)
                  setCadenasOpen(false)
                  setProvinciasOpen(false)
                  setEspOpen(false)
                }}
                className="px-6 py-4 text-sm transition-colors duration-200 focus:outline-none flex items-center gap-2"
                style={pais === p
                  ? { borderBottom: '3px solid #2fbc80', color: '#2fbc80', fontWeight: 700 }
                  : { borderBottom: '3px solid transparent', color: '#6b7280' }}
              >
                {p === 'Ecuador' ? '🇪🇨' : '🇵🇪'} {p === 'Peru' ? 'Perú' : p}
              </button>
            ))}
          </div>
        </div>

        {/* Estadisticas Generales */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 lg:p-6 border border-green-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Resumen General
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{d.total.toLocaleString('es-EC')}</p>
              <p className="text-sm text-gray-600 mt-1">Total Registros</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-green-600">{d.por_cadena.length}</p>
              <p className="text-sm text-gray-600 mt-1">Cadenas / Farmacias</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-blue-600">{d.por_provincia.length}</p>
              <p className="text-sm text-gray-600 mt-1">Provincias</p>
            </div>
          </div>
        </div>

        {/* Graficas */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* Por Cadena */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
                <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Distribución por Cadena
                <span className="text-xs text-gray-500 ml-2">(Top 10)</span>
              </h3>
              <HorizontalBarChart data={chartCadenas} />
            </div>

            {/* Por Provincia */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
                <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Distribución por Provincia
                <span className="text-xs text-gray-500 ml-2">(Top 10)</span>
              </h3>
              <HorizontalBarChart data={chartProvincias} />
            </div>
          </div>

          {/* Por Especialidad */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
              <svg className="w-5 h-5 text-[#8d82bc] mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              Distribución por Especialidad
            </h3>
            <HorizontalBarChart data={chartEspecialidades} />
          </div>
        </div>

        {/* Desglose por Especialidad */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setEspOpen(o => !o)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-[#8d82bc] transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-[#8d82bc] mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span className="text-base lg:text-lg">Desglose por Especialidad</span>
              </div>
              <svg className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${espOpen ? 'rotate-180' : ''}`}
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {espOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Especialidad</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Registros</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Distribución</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {d.por_especialidad.map(esp => (
                    <tr key={esp.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{esp.nombre}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{esp.total.toLocaleString('es-EC')}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-[#8d82bc]">{esp.porcentaje}%</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className="h-2.5 rounded-full" style={{ background: '#8d82bc', width: `${esp.porcentaje}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{d.total.toLocaleString('es-EC')}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-[#8d82bc]">100%</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Desglose por Cadena */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setCadenasOpen(o => !o)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-green-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span className="text-base lg:text-lg">Desglose por Cadena de Farmacia</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({d.por_cadena.length} cadenas)</span>
              </div>
              <svg className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${cadenasOpen ? 'rotate-180' : ''}`}
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {cadenasOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Cadena / Farmacia</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Registros</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Distribución</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {d.por_cadena.map(c => (
                    <tr key={c.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{c.nombre}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{c.total.toLocaleString('es-EC')}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-green-600">{c.porcentaje}%</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className="bg-green-600 h-2.5 rounded-full" style={{ width: `${c.porcentaje}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{d.total.toLocaleString('es-EC')}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-green-600">100%</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Desglose por Provincia */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setProvinciasOpen(o => !o)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-blue-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-base lg:text-lg">Desglose por Provincia</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({d.por_provincia.length} provincias)</span>
              </div>
              <svg className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${provinciasOpen ? 'rotate-180' : ''}`}
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {provinciasOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Provincia</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Registros</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Distribución</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {d.por_provincia.map(p => (
                    <tr key={p.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{p.nombre}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{p.total.toLocaleString('es-EC')}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-blue-600">{p.porcentaje}%</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${p.porcentaje}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{d.total.toLocaleString('es-EC')}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-blue-600">100%</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies — Base de Datos GSK Farmacias</p>
        </div>

      </div>
    </div>
  )
}
